import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import moment from 'moment';
import {
  BehaviorSubject,
  Observable,
  map,
  of,
  skipWhile,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { DeliveryLocation } from 'src/app/api/contracts/location';
import { WeekDay } from 'src/app/api/contracts/menu';
import { OrderType } from 'src/app/api/contracts/order';
import { Product } from 'src/app/api/contracts/product';
import { APP_CONFIG, AppConfig } from 'src/app/config/app-config';
import { CartService } from 'src/app/services/cart.service';
import { ClientService } from 'src/app/services/client.service';
import { MealMenuService } from 'src/app/services/meal-menu.service';
import { ModalService } from 'src/app/services/modal.service';
import { Cart } from 'src/app/types/cart';
import {
  getCurrentWeekNumber,
  getNextWeekNumber,
  getWeekFirstDay,
  getWeekLastDay,
} from 'src/app/utils/date.utils';
import { WEEK_DAYS } from 'src/constants/date.constants';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
})
export class ProductDetailsComponent implements OnInit {
  readonly orderType: OrderType;
  readonly selectedDay: WeekDay | null;
  readonly weeklyMenuId: number | null;
  readonly weekNumber: number | null;
  public readonly quantityInCart$: Observable<number> = of(0);
  public readonly clientLocation$: Observable<DeliveryLocation>;

  public readonly product$ = new BehaviorSubject<Product | null>(null);
  public readonly orderTypes = OrderType;
  public isProductUnavailable: boolean = false;
  public isOpenedAfterScanningBarcode: boolean = false;

  public readonly imagesUrl = environment.imagesUrl;

  constructor(
    private readonly modalService: ModalService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly menuService: MealMenuService,
    private readonly navCtrl: NavController,
    private readonly clientService: ClientService,
    private readonly cartService: CartService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig
  ) {
    const orderType = Number(
      this.route.snapshot.queryParams['type']
    ) as OrderType;
    if (orderType === undefined) {
      this.navCtrl.navigateRoot('store');
    }
    this.orderType = orderType;

    const day = this.route.snapshot.queryParams['day'] as WeekDay;
    this.selectedDay = day;
    if (!this.selectedDay && orderType === OrderType.DELIVERY) {
      this.navCtrl.navigateRoot('store');
    }

    const weeklyMenuId = parseInt(
      this.route.snapshot.queryParams['weeklyMenuId']
    );
    this.weeklyMenuId = weeklyMenuId;
    if (isNaN(this.weeklyMenuId) && orderType === OrderType.DELIVERY) {
      this.navCtrl.navigateRoot('store');
    }

    const weekNumber: number = parseInt(
      this.route.snapshot.queryParams['weekNumber']
    );
    this.weekNumber = weekNumber;
    if (isNaN(this.weekNumber) && orderType === OrderType.DELIVERY) {
      this.navCtrl.navigateRoot('store');
    }

    if (orderType === OrderType.IN_STORE) {
      this.selectedDay = null;
      this.weekNumber = null;
      this.weeklyMenuId = null;
    }

    this.quantityInCart$ = this.product$.pipe(
      skipWhile((x) => !x),
      switchMap((product) =>
        this.cartService.currentCart$.pipe(
          map((cart) => [cart, product!] as [Cart, Product])
        )
      ),
      map(([cart, product]: [Cart, Product]) => {
        const cartItem = cart.items.find(
          (item) =>
            item.product.id === product.id &&
            item.day === this.selectedDay &&
            item.weeklyMenuId === this.weeklyMenuId &&
            item.weekNumber === this.weekNumber
        );
        if (cartItem) {
          return cartItem.quantity;
        }
        return 0;
      })
    );

    this.clientLocation$ = this.clientService.clientLocation$;
  }

  ngOnInit() {
    this.checkIfProductOpenedFromScanningBarcode();
    this.loadProduct();
  }

  retrieveProduct(productId: number, weekNumber: number): Observable<Product> {
    if (this.orderType === OrderType.DELIVERY) {
      return this.menuService.weeklyMenus$.pipe(
        take(1),
        switchMap((menus) => {
          if (!menus) {
            return this.menuService.retrieveWeeklyMenus();
          }
          return of(menus);
        }),
        map(
          (menus) =>
            menus
              .find((menu) => menu.weekNumber === weekNumber)
              ?.days[this.selectedDay!]?.meals.find(
                (m) => m.id === productId
              ) as Product
        )
      );
    }

    if (this.orderType === OrderType.IN_STORE) {
      return this.clientService.clientLocation$.pipe(
        take(1),
        switchMap((clientLocation) =>
          this.menuService.retrieveInStoreProduct(
            productId,
            clientLocation.hash
          )
        )
      );
    }
    return throwError(() => new Error('product not found'));
  }

  onDescriptionInfoClick(): void {
    this.modalService
      .showProductDescriptionModal(this.product$.getValue()!)
      .subscribe((data) => {});
  }

  addToCart(): void {
    const product = this.product$.getValue()!;
    this.cartService.addItemToCart(
      product,
      this.orderType,
      this.selectedDay,
      this.weeklyMenuId,
      this.weekNumber
    );
  }

  increaseQuantity(quantity: number = 1): void {
    const product = this.product$.getValue()!;
    this.cartService.increaseAmount({
      product,
      day: this.selectedDay,
      weeklyMenuId: this.weeklyMenuId,
      weekNumber: this.weekNumber,
      quantity,
    });
  }

  decreaseQuantity(quantity: number): void {
    const product = this.product$.getValue()!;
    this.cartService.decreaseAmount({
      product,
      day: this.selectedDay,
      weeklyMenuId: this.weeklyMenuId,
      weekNumber: this.weekNumber,
      quantity,
    });
  }

  private loadProduct(): void {
    const productFromNavExtras =
      this.router.getCurrentNavigation()?.extras?.state?.['product'];
    if (productFromNavExtras) {
      this.product$.next(productFromNavExtras);
      this.checkProductAvailability();
      return;
    }
    const productId: number = parseInt(this.route.snapshot.params['id']);
    this.retrieveProduct(productId, this.weekNumber!).subscribe((product) => {
      this.product$.next(product);
      this.checkProductAvailability();
    });
  }

  private checkIfProductOpenedFromScanningBarcode(): void {
    const isOpenedAfterScanningBarcode =
      this.router.getCurrentNavigation()?.extras?.state?.[
        'isOpenedAfterScanningBarcode'
      ];
    this.isOpenedAfterScanningBarcode = isOpenedAfterScanningBarcode;
  }

  private checkProductAvailability(): void {
    const product = this.product$.getValue();
    if (!product) {
      this.isProductUnavailable = true;
    }
    const now = moment();
    const isProductUnavailable =
      (this.weekNumber === getCurrentWeekNumber() &&
        WEEK_DAYS.indexOf(this.selectedDay!) <= now.weekday()) ||
      (WEEK_DAYS.indexOf(this.selectedDay!) === now.weekday() + 1 &&
        now.hours() >= this.appConfig.stopReceivingOrdersAt) ||
      (this.weekNumber === getNextWeekNumber() &&
        this.selectedDay === getWeekFirstDay() &&
        now.weekday() === WEEK_DAYS.indexOf(getWeekLastDay()) &&
        now.hours() >= this.appConfig.stopReceivingOrdersAt);

    this.isProductUnavailable = isProductUnavailable;
  }
}
